import React, { useState, useEffect } from 'react';
import { useGoals } from '../../contexts/GoalContext';
import { useLast10 } from '../../contexts/Last10Context';
import { useCalendars } from '../../contexts/CalendarContext';
import GoalCard from './GoalCard';
import GoalModal from './GoalModal';
import GoalProgress from './GoalProgress';
import FinishScore from './FinishScore';
import FinishModeBanner from '../Common/FinishModeBanner';
import Button from '../Common/Button';
import { logGoalDeleted } from '../../services/analytics';

const GoalsList = () => {
    const { goals, loading, createGoal, updateGoal, deleteGoal, getGroupedGoals, getStats, filters, updateFilters, clearFilters } = useGoals();
    const { checkAndFlagAbandonment, handleGoalCompletedFromLast10 } = useLast10();
    const { selectedCalendar, canEdit } = useCalendars();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [viewProgressGoal, setViewProgressGoal] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Run abandonment check once goals are loaded
    useEffect(() => {
        if (goals.length > 0) {
            checkAndFlagAbandonment(goals);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goals.length]);

    const currentYear = new Date().getFullYear();
    const canEditGoals = selectedCalendar ? canEdit(selectedCalendar) : false;

    const handleCreateGoal = () => {
        setSelectedGoal(null);
        setIsModalOpen(true);
    };

    const handleEditGoal = (goal) => {
        setSelectedGoal(goal);
        setIsModalOpen(true);
    };

    const handleSaveGoal = async (goalData) => {
        try {
            if (selectedGoal) {
                // If transitioning to completed and was in last10, record it
                if (goalData.status === 'completed' && selectedGoal.last10EnteredAt) {
                    await handleGoalCompletedFromLast10(selectedGoal);
                }
                await updateGoal(selectedGoal.id, goalData);
            } else {
                await createGoal(goalData);
            }
            setIsModalOpen(false);
            setSelectedGoal(null);
        } catch (error) {
            console.error('Error saving goal:', error);
            throw error;
        }
    };

    const handleDeleteGoal = (goal) => {
        setDeleteConfirm(goal);
    };

    const confirmDelete = async () => {
        if (deleteConfirm) {
            try {
                await deleteGoal(deleteConfirm.id);
                logGoalDeleted({ period: deleteConfirm.period });
                setDeleteConfirm(null);
            } catch (error) {
                console.error('Error deleting goal:', error);
            }
        }
    };

    const handleViewTasks = (goal) => {
        setViewProgressGoal(goal);
    };

    const stats = getStats();
    const groupedGoals = getGroupedGoals();

    if (!selectedCalendar) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">
                    Please select a calendar to view goals
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header with Stats and Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Goals</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Track your progress towards your goals
                        </p>
                    </div>
                    {canEditGoals && (
                        <Button onClick={handleCreateGoal} variant="primary">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            New Goal
                        </Button>
                    )}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.total}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Total Goals</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.active}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Active</p>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.completed}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Completed</p>
                    </div>
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{stats.archived}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Archived</p>
                    </div>
                </div>

                {/* Finish Score — Last 10% user-level metric */}
                <FinishScore />

                {/* Filters */}
                <div className="flex flex-wrap gap-3">
                    <select
                        value={filters.period || ''}
                        onChange={(e) => updateFilters({ period: e.target.value || null })}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                        <option value="">All Periods</option>
                        <option value="yearly">Yearly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="monthly">Monthly</option>
                    </select>

                    <select
                        value={filters.year}
                        onChange={(e) => updateFilters({ year: parseInt(e.target.value) })}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                        {[...Array(5)].map((_, i) => {
                            const year = currentYear - 2 + i;
                            return <option key={year} value={year}>{year}</option>;
                        })}
                    </select>

                    <select
                        value={filters.status || ''}
                        onChange={(e) => updateFilters({ status: e.target.value || null })}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="archived">Archived</option>
                    </select>

                    <button
                        onClick={clearFilters}
                        className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* Finish Mode Notification Banner */}
            <FinishModeBanner />

            {/* Goals List */}
            {loading ? (
                <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400">Loading goals...</p>
                </div>
            ) : goals.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-md">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No goals</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by creating a new goal.
                    </p>
                    {canEditGoals && (
                        <div className="mt-6">
                            <Button onClick={handleCreateGoal} variant="primary">
                                Create Goal
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {/* ── Final Phase — goals at 80–99% ──────────────────────── */}
                    {(() => {
                        const finalPhaseGoals = goals.filter(g =>
                            g.last10EnteredAt &&
                            g.status !== 'completed' &&
                            !g.completedFromLast10
                        );
                        if (!finalPhaseGoals.length) return null;
                        return (
                            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/10 p-4">
                                <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-60" />
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                                    </span>
                                    Final Phase &mdash; {finalPhaseGoals.length} goal{finalPhaseGoals.length !== 1 ? 's' : ''} near completion
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {finalPhaseGoals.map(goal => (
                                        <GoalCard
                                            key={goal.id}
                                            goal={goal}
                                            onEdit={handleEditGoal}
                                            onDelete={handleDeleteGoal}
                                            onViewTasks={handleViewTasks}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Yearly Goals */}
                    {groupedGoals.yearly.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                                <span className="bg-blue-500 w-1 h-6 rounded mr-2"></span>
                                Yearly Goals
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {groupedGoals.yearly.map(goal => (
                                    <GoalCard
                                        key={goal.id}
                                        goal={goal}
                                        onEdit={handleEditGoal}
                                        onDelete={handleDeleteGoal}
                                        onViewTasks={handleViewTasks}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quarterly Goals */}
                    {groupedGoals.quarterly.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                                <span className="bg-purple-500 w-1 h-6 rounded mr-2"></span>
                                Quarterly Goals
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {groupedGoals.quarterly.map(goal => (
                                    <GoalCard
                                        key={goal.id}
                                        goal={goal}
                                        onEdit={handleEditGoal}
                                        onDelete={handleDeleteGoal}
                                        onViewTasks={handleViewTasks}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Monthly Goals */}
                    {groupedGoals.monthly.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                                <span className="bg-green-500 w-1 h-6 rounded mr-2"></span>
                                Monthly Goals
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {groupedGoals.monthly.map(goal => (
                                    <GoalCard
                                        key={goal.id}
                                        goal={goal}
                                        onEdit={handleEditGoal}
                                        onDelete={handleDeleteGoal}
                                        onViewTasks={handleViewTasks}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Goal Modal */}
            <GoalModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedGoal(null);
                }}
                goal={selectedGoal}
                onSave={handleSaveGoal}
            />

            {/* Goal Progress Modal */}
            {viewProgressGoal && (
                <GoalProgress
                    goal={viewProgressGoal}
                    onClose={() => setViewProgressGoal(null)}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div
                            className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
                            onClick={() => setDeleteConfirm(null)}
                        ></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                                        <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                                            Delete Goal
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Are you sure you want to delete "{deleteConfirm.title}"? This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                                <Button onClick={confirmDelete} variant="primary" className="bg-red-600 hover:bg-red-700">
                                    Delete
                                </Button>
                                <Button onClick={() => setDeleteConfirm(null)} variant="secondary">
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoalsList;
